import { useState, useCallback } from 'react';
import { ResultPod } from '../mathEngine';
import MathRenderer from '../../../components/base/MathRenderer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface NotebookEntry {
  id: string;
  query: string;
  pods: ResultPod[];
  timestamp: Date;
  note: string;
  pinned: boolean;
}

export interface ExamMistake {
  statement: string;
  userAnswer: string;
  correctAnswer: string;
  subject?: string;
}

interface NotebookPanelProps {
  show: boolean;
  entries: NotebookEntry[];
  examMistakes?: ExamMistake[];
  onClose: () => void;
  onUpdateNote: (id: string, note: string) => void;
  onDelete: (id: string) => void;
  onTogglePin: (id: string) => void;
  onClear: () => void;
}

// ─── PDF Export Helper ────────────────────────────────────────────────────────

function buildPrintHTML(entries: NotebookEntry[]): string {
  const sorted = [...entries].sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));

  const podHtml = (pods: ResultPod[]) =>
    pods.map(pod => {
      const subpods = pod.subpods
        .filter(s => s.plaintext)
        .map(s => `<p style="margin:4px 0;font-family:monospace;font-size:13px;color:#1e293b;">${s.plaintext}</p>`)
        .join('');
      if (!subpods) return '';
      return `
        <div style="margin-bottom:12px;">
          <strong style="font-size:12px;color:#7c3aed;text-transform:uppercase;letter-spacing:0.5px;">${pod.title}</strong>
          <div style="margin-top:4px;padding:8px;background:#f8f9fa;border-radius:6px;border-left:3px solid #7c3aed;">${subpods}</div>
        </div>`;
    }).join('');

  const items = sorted.map((entry, i) => `
    <div style="page-break-inside:avoid;margin-bottom:28px;padding:20px;border:1px solid #e2e8f0;border-radius:12px;background:#fff;">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px;border-bottom:1px solid #f1f5f9;padding-bottom:10px;">
        <span style="background:linear-gradient(135deg,#7c3aed,#a855f7);color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;flex-shrink:0;">${i + 1}</span>
        <div style="flex:1;">
          <div style="font-family:monospace;font-size:14px;font-weight:600;color:#1e293b;">${entry.query}</div>
          <div style="font-size:11px;color:#94a3b8;margin-top:2px;">${entry.timestamp.toLocaleString()}</div>
        </div>
        ${entry.pinned ? '<span style="font-size:11px;background:#fef3c7;color:#92400e;padding:2px 8px;border-radius:999px;font-weight:600;">★ Pinned</span>' : ''}
      </div>
      <div style="margin-bottom:${entry.note ? '14px' : '0'};">${podHtml(entry.pods)}</div>
      ${entry.note ? `<div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:8px;padding:12px;"><div style="font-size:11px;font-weight:700;color:#16a34a;margin-bottom:4px;">📝 NOTE</div><div style="font-size:13px;color:#166534;white-space:pre-wrap;">${entry.note}</div></div>` : ''}
    </div>`).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <title>Genius Math Notebook</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #f8fafc; color: #1e293b; padding: 40px; }
    @media print {
      body { background: #fff; padding: 20px; }
      .no-print { display: none !important; }
    }
  </style>
</head>
<body>
  <div style="max-width:800px;margin:0 auto;">
    <div style="text-align:center;margin-bottom:36px;padding:28px;background:linear-gradient(135deg,#7c3aed,#a855f7);border-radius:16px;color:#fff;">
      <div style="font-size:28px;font-weight:900;letter-spacing:-0.5px;">Genius Math Notebook</div>
      <div style="margin-top:6px;opacity:0.85;font-size:14px;">Exported on ${new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} · ${entries.length} entries</div>
    </div>
    ${items || '<p style="text-align:center;color:#94a3b8;">No entries to export.</p>'}
    <div style="margin-top:40px;text-align:center;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:16px;">Generated by Genius Math</div>
  </div>
  <button class="no-print" onclick="window.print()" style="position:fixed;bottom:24px;right:24px;background:linear-gradient(135deg,#7c3aed,#a855f7);color:#fff;border:none;padding:12px 24px;border-radius:10px;font-size:14px;font-weight:700;cursor:pointer;box-shadow:0 4px 14px rgba(124,58,237,0.4);">🖨️ Print / Save as PDF</button>
</body>
</html>`;
}

// ─── Cheat Sheet Builder ──────────────────────────────────────────────────────

function detectEntryTopic(query: string): string {
  const q = query.toLowerCase();
  if (/derivative|d\/dx/.test(q)) return 'Calculus — Derivatives';
  if (/integrat|∫/.test(q)) return 'Calculus — Integrals';
  if (/limit|lim/.test(q)) return 'Calculus — Limits';
  if (/taylor|series/.test(q)) return 'Calculus — Series';
  if (/solve|equation/.test(q)) return 'Algebra — Equations';
  if (/factor/.test(q)) return 'Algebra — Factoring';
  if (/simplify|expand/.test(q)) return 'Algebra — Simplification';
  if (/mean|median|std|variance|statistic/.test(q)) return 'Statistics';
  if (/geometry|area|volume|circle|sphere|cylinder/.test(q)) return 'Geometry';
  return 'General';
}

function buildCheatSheetHTML(entries: NotebookEntry[], mistakes: ExamMistake[]): string {
  // Group entries by topic
  const grouped: Record<string, NotebookEntry[]> = {};
  entries.forEach(e => {
    const topic = detectEntryTopic(e.query);
    if (!grouped[topic]) grouped[topic] = [];
    grouped[topic].push(e);
  });

  const topicSections = Object.entries(grouped).map(([topic, items]) => {
    const cards = items.map(entry => {
      const primaryPod = entry.pods.find(p => ['Result', 'Derivative', 'Solutions', 'Exact result', 'Simplified Form', 'Factored Form'].includes(p.title)) || entry.pods[0];
      const result = primaryPod?.subpods?.[0]?.plaintext ?? '';
      return `
        <div style="padding:12px;background:#f8fafc;border-radius:8px;border-left:3px solid #7c3aed;margin-bottom:10px;">
          <div style="font-family:monospace;font-size:12px;font-weight:700;color:#1e293b;margin-bottom:4px;">${entry.query}</div>
          <div style="font-size:11px;color:#6366f1;font-family:monospace;">${result.replace(/\$\$/g, '').replace(/\$/g, '')}</div>
          ${entry.note ? `<div style="margin-top:6px;font-size:10px;color:#16a34a;background:#f0fdf4;padding:4px 8px;border-radius:4px;">💡 ${entry.note}</div>` : ''}
        </div>`;
    }).join('');
    return `
      <div style="break-inside:avoid;margin-bottom:24px;">
        <div style="background:linear-gradient(90deg,#7c3aed,#a855f7);color:#fff;padding:8px 16px;border-radius:8px 8px 0 0;font-size:13px;font-weight:700;">${topic}</div>
        <div style="border:1px solid #e2e8f0;border-top:none;border-radius:0 0 8px 8px;padding:12px;">${cards}</div>
      </div>`;
  }).join('');

  const mistakesSection = mistakes.length > 0 ? `
    <div style="break-inside:avoid;margin-bottom:24px;">
      <div style="background:linear-gradient(90deg,#dc2626,#ef4444);color:#fff;padding:8px 16px;border-radius:8px 8px 0 0;font-size:13px;font-weight:700;">⚠ Review These — Exam Mistakes</div>
      <div style="border:1px solid #fecaca;border-top:none;border-radius:0 0 8px 8px;padding:12px;">
        ${mistakes.map(m => `
          <div style="padding:10px;background:#fff5f5;border-radius:6px;border-left:3px solid #ef4444;margin-bottom:8px;">
            <div style="font-size:12px;font-weight:700;color:#1e293b;margin-bottom:4px;">${m.statement}</div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:11px;">
              <div style="background:#fff;border-radius:4px;padding:6px;border:1px solid #fecaca;">
                <div style="color:#dc2626;font-weight:700;margin-bottom:2px;">Your answer:</div>
                <div style="font-family:monospace;color:#7f1d1d;">${m.userAnswer || '(skipped)'}</div>
              </div>
              <div style="background:#fff;border-radius:4px;padding:6px;border:1px solid #bbf7d0;">
                <div style="color:#16a34a;font-weight:700;margin-bottom:2px;">Correct:</div>
                <div style="font-family:monospace;color:#14532d;">${m.correctAnswer}</div>
              </div>
            </div>
          </div>`).join('')}
      </div>
    </div>` : '';

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>Genius Math Cheat Sheet</title>
  <style>
    * { box-sizing:border-box; margin:0; padding:0; }
    body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; background:#f8fafc; padding:32px; color:#1e293b; }
    .grid { display:grid; grid-template-columns:1fr 1fr; gap:20px; max-width:1100px; margin:0 auto; }
    @media print { body { background:#fff; padding:16px; } .no-print { display:none!important; } @page { margin:1.5cm; } }
  </style>
</head>
<body>
  <div style="max-width:1100px;margin:0 auto;">
    <div style="text-align:center;margin-bottom:32px;padding:24px;background:linear-gradient(135deg,#7c3aed,#a855f7);border-radius:16px;color:#fff;">
      <div style="font-size:26px;font-weight:900;">Genius Math — Cheat Sheet</div>
      <div style="margin-top:4px;font-size:13px;opacity:0.85;">Generated ${new Date().toLocaleDateString('en-US',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} · ${entries.length} formulas${mistakes.length > 0 ? ` · ${mistakes.length} exam mistakes` : ''}</div>
    </div>
    ${mistakes.length > 0 ? `<div style="margin-bottom:24px;">${mistakesSection}</div>` : ''}
    <div class="grid">${topicSections || '<p style="text-align:center;color:#94a3b8;grid-column:1/-1;">No notebook entries to export.</p>'}</div>
    <div style="margin-top:32px;text-align:center;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;padding-top:14px;">Genius Math Cheat Sheet</div>
  </div>
  <button class="no-print" onclick="window.print()" style="position:fixed;bottom:24px;right:24px;background:linear-gradient(135deg,#7c3aed,#a855f7);color:#fff;border:none;padding:12px 20px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;">🖨 Print / Save PDF</button>
</body>
</html>`;
}

// ─── Entry Card ───────────────────────────────────────────────────────────────

function EntryCard({
  entry,
  onUpdateNote,
  onDelete,
  onTogglePin,
}: {
  entry: NotebookEntry;
  onUpdateNote: (id: string, note: string) => void;
  onDelete: (id: string) => void;
  onTogglePin: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState(entry.note);

  const handleSaveNote = () => {
    onUpdateNote(entry.id, noteText);
    setEditingNote(false);
  };

  const primaryPod = entry.pods.find(p =>
    ['Result', 'Results', 'Exact result', 'Solutions', 'Derivative',
      'Simplified Form', 'Factored Form'].includes(p.title)
  ) || entry.pods[0];

  return (
    <div className={`rounded-xl border-2 overflow-hidden transition-all ${entry.pinned ? 'border-amber-400 bg-amber-50/50' : 'border-slate-200 bg-white'}`}>
      {/* Card Header */}
      <div className="flex items-start gap-3 p-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {entry.pinned && <i className="ri-pushpin-2-fill text-amber-500 text-xs"></i>}
            <div className="text-xs text-slate-400">
              {entry.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div className="text-sm font-mono font-semibold text-slate-800 truncate">{entry.query}</div>
          {primaryPod && !expanded && (
            <MathRenderer
              content={primaryPod.subpods?.[0]?.plaintext ?? ''}
              className="text-xs text-slate-500 mt-1 line-clamp-1"
            />
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onTogglePin(entry.id)}
            title={entry.pinned ? 'Unpin' : 'Pin'}
            className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${entry.pinned ? 'bg-amber-100 text-amber-600' : 'hover:bg-slate-100 text-slate-400'}`}
          >
            <i className={entry.pinned ? 'ri-pushpin-2-fill' : 'ri-pushpin-2-line'}></i>
          </button>
          <button
            onClick={() => setExpanded(v => !v)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 transition-colors cursor-pointer"
          >
            <i className={`ri-arrow-${expanded ? 'up' : 'down'}-s-line`}></i>
          </button>
          <button
            onClick={() => onDelete(entry.id)}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
          >
            <i className="ri-delete-bin-5-line"></i>
          </button>
        </div>
      </div>

      {/* Expanded Body */}
      {expanded && (
        <div className="border-t border-slate-200 px-4 pb-4 pt-3 space-y-3">
          {/* Pods */}
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {entry.pods.map((pod, i) => (
              <div key={i} className="text-xs">
                <div className="font-bold text-violet-600 mb-0.5">{pod.title}</div>
                {pod.subpods.filter(s => s.plaintext).map((sp, si) => (
                  <MathRenderer key={si} content={sp.plaintext ?? ''} className="text-slate-700 ml-2" />
                ))}
              </div>
            ))}
          </div>

          {/* Note Section */}
          <div className="border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-slate-600 flex items-center gap-1.5">
                <i className="ri-sticky-note-2-line text-emerald-500"></i>
                Annotation
              </span>
              {!editingNote && (
                <button
                  onClick={() => setEditingNote(true)}
                  className="text-xs text-violet-600 hover:text-violet-800 font-semibold cursor-pointer flex items-center gap-1"
                >
                  <i className="ri-edit-2-line text-xs"></i>
                  {entry.note ? 'Edit' : 'Add Note'}
                </button>
              )}
            </div>
            {editingNote ? (
              <div className="space-y-2">
                <textarea
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  placeholder="Write your notes, key takeaways, or observations…"
                  rows={3}
                  maxLength={500}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
                />
                <div className="flex justify-between items-center">
                  <span className="text-[10px] text-slate-400">{noteText.length}/500</span>
                  <div className="flex gap-2">
                    <button onClick={() => { setNoteText(entry.note); setEditingNote(false); }} className="px-3 py-1 text-xs text-slate-500 hover:text-slate-700 cursor-pointer">Cancel</button>
                    <button onClick={handleSaveNote} className="px-3 py-1 text-xs bg-violet-600 text-white rounded-lg font-semibold hover:bg-violet-700 cursor-pointer">Save</button>
                  </div>
                </div>
              </div>
            ) : entry.note ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-800 whitespace-pre-wrap">{entry.note}</div>
            ) : (
              <div className="text-xs text-slate-400 italic">No annotation yet</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main NotebookPanel ───────────────────────────────────────────────────────

export default function NotebookPanel({
  show,
  entries,
  examMistakes = [],
  onClose,
  onUpdateNote,
  onDelete,
  onTogglePin,
  onClear,
}: NotebookPanelProps) {
  const [search, setSearch] = useState('');

  const sorted = [...entries]
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return b.pinned ? 1 : -1;
      return b.timestamp.getTime() - a.timestamp.getTime();
    })
    .filter(e => !search.trim() || e.query.toLowerCase().includes(search.toLowerCase()));

  const handleExport = useCallback(() => {
    const html = buildPrintHTML(entries);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  }, [entries]);

  const handleCheatSheet = useCallback(() => {
    const html = buildCheatSheetHTML(entries, examMistakes);
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(html);
    win.document.close();
  }, [entries, examMistakes]);

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20" onClick={onClose}></div>

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-96 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-700 shadow-2xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-violet-600 to-fuchsia-600">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-book-open-line text-white"></i>
            </div>
            <div>
              <h3 className="font-bold text-white">Notebook</h3>
              <p className="text-violet-200 text-xs">{entries.length} saved result{entries.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleCheatSheet}
              title="Export Cheat Sheet"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-clipboard-line text-xs"></i>
              Cheat Sheet
            </button>
            <button
              onClick={handleExport}
              title="Export Notebook as PDF"
              className="flex items-center gap-1 px-2.5 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-file-pdf-2-line text-xs"></i>
              PDF
            </button>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-white cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Exam Mistakes Badge */}
        {examMistakes.length > 0 && (
          <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800/50 flex items-center gap-2">
            <i className="ri-error-warning-line text-red-500 text-sm"></i>
            <span className="text-xs text-red-700 dark:text-red-300 font-semibold">{examMistakes.length} exam mistake{examMistakes.length > 1 ? 's' : ''} tracked</span>
            <span className="text-xs text-red-500 dark:text-red-400 ml-1">— included in Cheat Sheet</span>
          </div>
        )}

        {/* Search */}
        {entries.length > 0 && (
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg">
              <i className="ri-search-line text-slate-400 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search entries…"
                className="flex-1 bg-transparent text-sm text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none"
              />
              {search && (
                <button onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Entries List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16 text-center">
              <div className="w-16 h-16 bg-violet-100 dark:bg-violet-900/30 rounded-2xl flex items-center justify-center mb-4">
                <i className="ri-book-open-line text-3xl text-violet-400"></i>
              </div>
              <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-2">Notebook is empty</h4>
              <p className="text-sm text-slate-400 max-w-52">
                After solving a problem, click <strong>Save to Notebook</strong> in the results bar to collect it here.
              </p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-400">No entries match your search</div>
          ) : (
            sorted.map(entry => (
              <EntryCard
                key={entry.id}
                entry={entry}
                onUpdateNote={onUpdateNote}
                onDelete={onDelete}
                onTogglePin={onTogglePin}
              />
            ))
          )}
        </div>

        {/* Footer */}
        {entries.length > 0 && (
          <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
            <div className="text-xs text-slate-500 dark:text-slate-400">{entries.filter(e => e.pinned).length} pinned</div>
            <button
              onClick={onClear}
              className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 px-3 py-1.5 rounded-lg transition-colors cursor-pointer"
            >
              <i className="ri-delete-bin-5-line mr-1"></i>
              Clear All
            </button>
          </div>
        )}
      </div>
    </>
  );
}
